import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { validate as validateUUID } from 'uuid';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { CustomMenuComponent } from '@connect-services/client/components/custom-menu/custom-menu.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { SaveEditModeResultEnum } from '@connect-services/client/models/common.model';
import { EditModeSaveService } from '@connect-services/client/services/edit-mode-save.service';
import {
    EditModeStateService,
    EditState,
    EntityEdit,
    LayerSnapshot,
} from '@connect-services/client/services/edit-mode-state.service';
import { ExportEditEntityService } from '@connect-services/client/services/export-edit-entity.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import {
    EditConflicts,
    EditConflictsDeletedEntitiesInner,
    EditModeUpsertResult,
    Layer,
    LayersService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

enum SaveEditModeSteps {
    CONFIRM = 1,
    SAVING = 2,
    SUMMARY = 3,
}

export type ModalData = {
    canEndEditSession: boolean;
    onSaved?: () => void | Promise<void>;
};

type SaveOption = {
    errorSnackKey: string;
    keepEditSessionOpen: boolean;
    successSnackKey: string;
};

type ExportableLayerEdits = {
    entityEdits: EntityEdit[];
    layerId: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, ComplexModalTemplate, CustomMenuComponent, LoadingComponent, RessourcesModule],
    selector: 'app-save-edit-mode-changes',
    templateUrl: './save-edit-mode-changes.component.html',
})
export class SaveEditModeChangesComponent implements OnInit {
    protected readonly currentStep = signal<SaveEditModeSteps>(SaveEditModeSteps.CONFIRM);
    protected editConflicts?: EditConflicts;
    protected readonly editResults = signal<EditModeUpsertResult[]>([]);
    protected readonly exportableEditsByLayer = computed<ExportableLayerEdits[]>(() => {
        const projectState: EditState | undefined = this.projectState();

        if (!projectState) {
            return [];
        }

        return Object.entries(projectState).reduce(
            (exportableEdits: ExportableLayerEdits[], [layerId, layerState]: [string, LayerSnapshot]) => {
                const entityEdits: EntityEdit[] = Object.values(layerState).filter(
                    (entityEdit: EntityEdit) => entityEdit.saveOrder !== -1,
                );

                if (entityEdits.length > 0) {
                    exportableEdits.push({
                        entityEdits,
                        layerId,
                    });
                }

                return exportableEdits;
            },
            [],
        );
    });
    protected hasConflicts = false;
    protected hasDeletedDeposits = false;
    protected readonly isExportLoading = signal<boolean>(false);
    protected readonly isLoading = signal<boolean>(false);
    protected readonly modalResult = signal<SaveEditModeResultEnum>(SaveEditModeResultEnum.CANCEL);
    protected readonly modifiedEntityEditCount = computed<number>(() =>
        this.exportableEditsByLayer().reduce(
            (total: number, { entityEdits }: ExportableLayerEdits) => total + entityEdits.length,
            0,
        ),
    );
    protected readonly modifiedLayerEditCount = computed<number>(() => this.exportableEditsByLayer().length);
    protected readonly modifiedLayerName = computed<string | undefined>(() => {
        const modifiedLayers: ExportableLayerEdits[] = this.exportableEditsByLayer();

        if (modifiedLayers.length !== 1) {
            return undefined;
        }

        const modifiedLayerId: string = modifiedLayers[0].layerId;

        return this.layerListBloc.value()?.items.find((layer: Layer) => layer.id === modifiedLayerId)?.name;
    });
    protected readonly title = signal<string>('editMode.submit.saveModalTitle');
    protected readonly usersDeletedEntitiesConflict = signal<string[]>([]);
    protected readonly usersModifiedEntitiesConflict = signal<string[]>([]);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected data = inject<ModalData>(MAT_DIALOG_DATA);
    protected dialogRef = inject(MatDialogRef<SaveEditModeChangesComponent, SaveEditModeResultEnum>);
    protected readonly editModeSaveService = inject(EditModeSaveService);
    protected readonly editModeStateService = inject(EditModeStateService);
    protected readonly ModalType = ModalType;
    protected readonly SaveEditModeResultEnum = SaveEditModeResultEnum;
    protected readonly SaveEditModeSteps = SaveEditModeSteps;
    protected readonly snackBarService = inject(SnackBarService);

    private readonly projectState = signal<EditState | undefined>(undefined);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly exportEditEntityService = inject(ExportEditEntityService);
    private readonly layerListBloc = inject(LayerListBloc);
    private readonly layersBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    private readonly layersService = inject(LayersService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);

    public async ngOnInit(): Promise<void> {
        const editConflicts = await this.getEditConflicts();
        if (!editConflicts) {
            return;
        }

        this.editConflicts = editConflicts;

        this.hasConflicts =
            !!editConflicts.deletedEntities.length ||
            !!editConflicts.layerIdsWithoutDeposit.length ||
            !!editConflicts.modifiedEntities.length;

        this.usersModifiedEntitiesConflict.set(this.getUniqueConflictsFullName(editConflicts.modifiedEntities));
        this.usersDeletedEntitiesConflict.set(this.getUniqueConflictsFullName(editConflicts.deletedEntities));
        this.hasDeletedDeposits = !!editConflicts.layerIdsWithoutDeposit.length;

        this.title.set(this.getTitle());
    }

    protected closeModal(): void {
        this.dialogRef.close(this.modalResult());
    }

    protected async exportInXlsx(isResult: boolean): Promise<void> {
        const project = this.projectBloc.value();
        const projectState = this.projectState();

        if (!project || !projectState) {
            return;
        }

        this.isExportLoading.set(true);

        if (isResult) {
            await this.exportEditEntityService.exportResultInXlsx(project, projectState, this.editResults());
        } else {
            await this.exportEditEntityService.exportConflictsInXlsx(project, projectState, this.editConflicts);
        }

        this.isExportLoading.set(false);
    }

    protected async save(saveEditModeResult: SaveEditModeResultEnum, saveOption: SaveOption): Promise<void> {
        if (!this.data.canEndEditSession) {
            return;
        }

        const saveResult = await this.runSave(saveOption);

        if (!saveResult.succeed) {
            this.dialogRef.close();

            return;
        }

        this.editResults.set(saveResult.editResults);
        this.modalResult.set(saveEditModeResult);
    }

    protected saveOnly(): Promise<void> {
        if (this.isLoading()) {
            return Promise.resolve();
        }

        return this.save(SaveEditModeResultEnum.SAVED_CONTINUE, {
            errorSnackKey: 'editMode.saveLayerError',
            keepEditSessionOpen: true,
            successSnackKey: 'editMode.saveLayerSuccess',
        });
    }

    private getEditConflicts(): Promise<EditConflicts | undefined> {
        const project = this.projectBloc.value();
        if (!project) {
            return Promise.resolve(undefined);
        }

        const editSessionInfo = this.projectEditModeService.getEditSession();
        if (!editSessionInfo) {
            return Promise.resolve(undefined);
        }

        const projectState = this.editModeStateService.getProjectState();

        this.projectState.set(projectState);

        const layerIds = Object.keys(projectState).filter((key: string) => validateUUID(key));

        const entityIds = Object.values(projectState).flatMap((layerSnapshot: LayerSnapshot) =>
            Object.keys(layerSnapshot),
        );

        const layerIdsSet = new Set(layerIds);
        const entityIdsSet = new Set(entityIds);

        if (!layerIdsSet.size || !entityIdsSet.size) {
            return Promise.resolve(undefined);
        }

        return firstValueFrom(
            this.layersService.layerControllerGetEditConflicts({
                editSessionStartDate: editSessionInfo.startDate,
                entityIds: [...entityIdsSet],
                layerIds: [...layerIdsSet],
                projectId: project.id,
            }),
        );
    }

    private getTitle(): string {
        if (!this.editConflicts || !this.hasConflicts) {
            return 'editMode.submit.saveModalTitle';
        }

        const hasModifiedEntities = !!this.editConflicts.modifiedEntities.length;
        const hasDeletedEntities = !!this.editConflicts.deletedEntities.length;
        const hasLayerIdsWithoutDeposit = !!this.editConflicts!.layerIdsWithoutDeposit.length;

        if (hasLayerIdsWithoutDeposit) {
            return 'editMode.submit.depositDeletedConflict';
        }

        if (hasModifiedEntities && hasDeletedEntities) {
            return 'editMode.submit.entitiesModifiedAndDeletedConflict';
        }

        if (hasModifiedEntities) {
            return 'editMode.submit.entitiesModifiedConflict';
        }

        return 'editMode.submit.entitiesDeletedConflict';
    }

    private getUniqueConflictsFullName(entitiesConflicts: EditConflictsDeletedEntitiesInner[]): string[] {
        return [...new Set(entitiesConflicts.map((entityConflict) => entityConflict.fullName ?? ''))];
    }

    private async runSave(options: SaveOption): Promise<{ editResults: EditModeUpsertResult[]; succeed: boolean }> {
        this.currentStep.set(SaveEditModeSteps.SAVING);
        this.isLoading.set(true);

        try {
            const editResults = await this.editModeSaveService.saveChanges({
                editConflicts: this.editConflicts,
                keepEditSessionOpen: options.keepEditSessionOpen,
            });

            this.layersBroadcastChannel.postMessage({
                additionalData: { layerIds: [] },
                isClosing: false,
                message: BroadcastChannelMessageEnum.ENTITIES_SAVED,
                source: BroadcastChannelSourceEnum.LAYER,
            } satisfies BroadcastChannelEvent<{ layerIds: string[] | null }>);

            await this.data.onSaved?.();

            if (editResults.some((editResult: EditModeUpsertResult) => !editResult.success)) {
                this.snackBarService.openErrorSnackBar('editMode.changesNotSavedDownloadReport');
            } else {
                this.snackBarService.openSuccessSnackBar(options.successSnackKey);
            }

            return { editResults, succeed: true };
        } catch {
            this.snackBarService.openErrorSnackBar(options.errorSnackKey);

            return { editResults: [], succeed: false };
        } finally {
            this.isLoading.set(false);
            this.currentStep.set(SaveEditModeSteps.SUMMARY);
        }
    }
}
